/**
 * PII redaction primitives (#4244, epic #759).
 *
 * Two complementary layers, same as the Rollbar provider in
 * `packages/error-tracking/src/providers/pii-scrub.ts` (which now imports
 * the primitives from this module to keep a single source of truth):
 *
 * 1. `redactPII(value)` -- recursive key-name redaction. Walks plain
 *    objects and arrays, replacing any value whose KEY matches a known
 *    PII field with the `[FILTERED]` sentinel. Cycles are handled via a
 *    `WeakSet`. Primitives pass through untouched.
 *
 * 2. `scrubPiiInString(input)` -- content regex pass. Detects PII shapes
 *    in free-form text (email, SSN, US/E.164 phone, Luhn-valid card
 *    number, bank-account candidates, drivers license, DOB-equivalents)
 *    and replaces each with a typed `[FILTERED:<kind>]` sentinel. As of
 *    #9911 this also detects secret/credential shapes (Stripe secret
 *    keys, webhook signing secrets, Bearer tokens, keyword-gated generic
 *    tokens) -- same "detect the shape, not the key name" design.
 *
 * Order in `scrubPiiInString` matters and is documented inline.
 *
 * Audit trail:
 *   - `packages/error-tracking/src/providers/pii-scrub.ts` previously
 *     owned the email/SSN/phone/card primitives. That module now wraps
 *     this one for Rollbar's `transform` hook.
 *   - This module additionally covers the categories #4244 calls out
 *     (bank-account, drivers license, DOB-equivalents) so the AI chat
 *     write path can apply a single chokepoint at insert time.
 *
 * The library is locale-agnostic. Refusal copy localization is owned by
 * the consumer (the chat route uses `formatRefusal` from
 * `./chat-safety.ts` and substitutes a Spanish body when the request
 * locale is `es`).
 */

const FILTERED = '[FILTERED]';

/* ──────────────────────────────────────────────────────────────────────── *
 * Key-name redaction (recursive over plain objects + arrays)
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Field names that are redacted in key-name passes, regardless of value
 * type. Lower-cased for case-insensitive matching. The set is exported
 * as a readonly array so the Rollbar `scrubFields` config can also
 * consume it.
 *
 * The list combines:
 *   - Identity / contact (email, phone, name).
 *   - Address (street, mailing).
 *   - Government IDs (ssn, dob, drivers license).
 *   - Financial (bank account, ABA routing, generic account number).
 *
 * Extend by adding the new key in both `camelCase` and `snake_case`
 * forms so callers don't have to think about the casing.
 */
export const PII_SCRUB_FIELDS: readonly string[] = [
  // Identity / contact
  'email',
  'phone',
  'phoneNumber',
  'phone_number',
  'recipient',
  'recipientEmail',
  'recipient_email',
  'firstName',
  'lastName',
  'first_name',
  'last_name',
  'fullName',
  'full_name',
  'tenantEmail',
  'ownerEmail',
  'vendorEmail',
  'signerEmail',
  'signer_email',
  // Address
  'mailingStreet',
  'mailing_street',
  'streetAddress',
  'street_address',
  'address',
  'address1',
  'address2',
  // Government IDs
  'ssn',
  'dob',
  'dateOfBirth',
  'date_of_birth',
  'driversLicense',
  'drivers_license',
  'dl',
  'dlNumber',
  'dl_number',
  // Financial
  'bankAccount',
  'bank_account',
  'accountNumber',
  'account_number',
  'aba',
  'routing',
  'routingNumber',
  'routing_number',
];

const SCRUB_KEY_SET = new Set(PII_SCRUB_FIELDS.map((k) => k.toLowerCase()));

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Recursively redact known-PII keys from a value. Returns a new value;
 * the input is not mutated. Cycles return the `[FILTERED]` sentinel via
 * a `WeakSet` seen-set rather than recursing infinitely.
 *
 * Only key-name redaction. String-content scrubbing happens separately
 * in `scrubPiiInString` and is applied only to specific subtrees that
 * are known to carry free-form text.
 */
export function redactPII(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (Array.isArray(value)) {
    if (seen.has(value)) return FILTERED;
    seen.add(value);
    return value.map((item) => redactPII(item, seen));
  }

  if (isPlainObject(value)) {
    if (seen.has(value)) return FILTERED;
    seen.add(value);
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value)) {
      if (SCRUB_KEY_SET.has(key.toLowerCase())) {
        out[key] = FILTERED;
      } else {
        out[key] = redactPII(v, seen);
      }
    }
    return out;
  }

  return value;
}

/* ──────────────────────────────────────────────────────────────────────── *
 * String-content PII regexes
 *
 * Order in `scrubPiiInString` matters:
 *   1. Secrets (Stripe key / webhook secret / Bearer token / keyword-gated
 *      generic token) -- FIRST, so a token containing a 13-19-digit run is
 *      redacted as a secret before the card scrubber misclassifies it.
 *   2. Card numbers (most specific via Luhn gate)
 *   3. SSN (3-2-4 hyphenated)
 *   4. DOB-equivalents (date adjacent to dob/birth/born keyword)
 *   5. Drivers license (state-aware + generic fallback)
 *   6. Bank account (ABA-routing or DDA candidate; chat-context heuristic)
 *   7. Email (medium specificity)
 *   8. Phone (loosest; would otherwise eat digits inside other patterns)
 *
 * Each replacement uses a typed `[FILTERED:<kind>]` sentinel so callers
 * (and audits) can tell which class fired without giving attackers
 * length information.
 *
 * UUID tokens are opaque to steps 2-8, but NOT to step 1 (#10657). The
 * secrets pass runs over the whole string first, so a uuid used AS a
 * credential (`Bearer <uuid>`, `api_key=<uuid>`) is still redacted. Then
 * every standalone 8-4-4-4-12 hex token is emitted verbatim and only the
 * text between them goes through the PII classes. A phone, card, SSN or
 * account number is never written inside a uuid grouping, but the digit
 * runs of random uuids matched the phone, card, ABA and (uppercase) DL
 * regexes: 1.35% of lowercase and 2.08% of uppercase uuids were rewritten,
 * which broke append-only audit links and the tool args an approved AI
 * proposal re-parses to execute.
 * ──────────────────────────────────────────────────────────────────────── */

// A standalone uuid. The boundaries exclude alphanumerics, so `id=<uuid>`,
// `/leases/<uuid>` and `<uuid>-5507` are all tokens while a uuid fused to
// other letters or digits is not. A uuid inside an email address is never
// carved out -- see `scrubPiiInString`.
const UUID_TOKEN_RE =
  /(?<![0-9A-Za-z])[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}(?![0-9A-Za-z])/g;

// Stripe secret-key family: sk_live_/sk_test_/rk_live_/rk_test_. pk_
// (publishable) is deliberately excluded -- those are meant to be
// client-visible and redacting them would be actively wrong (they appear
// in NEXT_PUBLIC_* env vars and client-side Stripe.js init code).
const STRIPE_SECRET_KEY_RE = /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{16,}\b/g;

// Stripe webhook signing secret.
const STRIPE_WEBHOOK_SECRET_RE = /\bwhsec_[A-Za-z0-9]{16,}\b/g;

// HTTP Authorization: Bearer <token> scheme value.
const BEARER_TOKEN_RE = /\bBearer\s+[A-Za-z0-9\-._~+/]{16,}=*/g;

// Generic keyword-gated secret: a long alphanumeric run immediately after
// an api-key/token/secret-signaling keyword and an assignment/colon
// separator. Same "detect the shape via an adjacent keyword, not the key
// name" trade-off as BANK_KEYWORD_RE/DL_KEYWORD_RE above -- a false
// positive costs far less than a missed credential. The 20-char floor
// keeps short, non-secret `token: "expired"`-style values out.
const SECRET_KEYWORDS =
  '(?:api[_-]?key|access[_-]?token|auth[_-]?token|client[_-]?secret|secret[_-]?key|token|secret)';
const SECRET_KEYWORD_RE = new RegExp(
  `${SECRET_KEYWORDS}\\s*[:=]\\s*['"]?([A-Za-z0-9_-]{20,})['"]?`,
  'gi',
);

// Email: standard local@domain.tld. Allows + tags and dots in the local part.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// US SSN: 3-2-4 with hyphens. Unhyphenated 9-digit runs are intentionally
// NOT matched here -- they would also catch phone numbers, ZIP+4
// combinations, and order IDs. The hyphenated form is the audit-required
// shape; the LLM fallback layer is the safety net for the unhyphenated edge.
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;

// Phone candidates -- Luhn-style digit-count gate is implicit in the regex
// (10 digits for US, 7-15 for E.164). Two shapes:
// - E.164 with explicit +country prefix (+15551234567)
// - US 10-digit, optionally formatted (555) 123-4567 / 555-123-4567 / 5551234567
const E164_RE = /\+\d{1,3}[\s().-]?\d{1,4}[\s().-]?\d{1,4}[\s().-]?\d{2,9}/g;
const US_PHONE_RE =
  /(?<![\d.])(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?![\d.])/g;

// Card number candidate: 13-19 digits, optionally separated by spaces or
// hyphens. Final accept gated by Luhn check in the replacer.
const CARD_CANDIDATE_RE = /\b(?:\d[ -]?){12,18}\d\b/g;

// DOB-equivalent: a date in any of four formats, ONLY when adjacent to a
// keyword that signals it is a birth date. Standalone dates do NOT match
// because they would catch lease dates, move-in dates, invoice dates, etc.
// Adjacency is intentionally generous (10 chars in either direction) so
// "dob: 03/14/1990" matches and "DOB is March 14, 1990" matches, but a
// bare "03/14/1990" in a lease addendum does not.
const DOB_KEYWORDS = '(?:dob|d\\.o\\.b\\.?|date\\s+of\\s+birth|birth\\s*date|birthday|born)';
const DOB_DATE_RAW =
  '(?:\\d{1,2}[/.-]\\d{1,2}[/.-]\\d{2,4}|\\d{4}-\\d{1,2}-\\d{1,2}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+\\d{4})';
const DOB_RE = new RegExp(
  `${DOB_KEYWORDS}[^\\w\\n]{0,10}${DOB_DATE_RAW}|${DOB_DATE_RAW}[^\\w\\n]{0,10}${DOB_KEYWORDS}`,
  'gi',
);

// Drivers license: state-prefix + alphanumeric block. Covers the
// common-format ~20 US states; followed by a generic fallback when the
// candidate is preceded by a DL-signaling keyword. The keyword-gated
// fallback is necessary because the formats are ambiguous in isolation
// (CA drivers license = "A1234567" looks like an order ID).
const DL_KEYWORDS =
  '(?:dl|d\\.l\\.?|driver\'?s?\\s*licen[cs]e|license\\s*#|license\\s*number|driver\\s+id)';
// Keyword-gated generic DL: letter + 6-13 alphanumerics, anywhere near a
// DL-signaling keyword. Captures any state-specific format the bare
// pattern below would miss.
const DL_KEYWORD_RE = new RegExp(
  `${DL_KEYWORDS}\\s*[:#]?\\s*([A-Z]{1,2}[0-9A-Z]{4,12}[0-9])`,
  'gi',
);
// Bare state-prefixed pattern for the common formats (rough sketch; the
// keyword-gated regex above is the primary detector).
const DL_BARE_RE = /\b(?:CA|TX|FL|NY|IL|PA|OH|GA|NC|MI|NJ|VA|WA|AZ|MA|TN|IN|MO|MD|WI)[-\s]?[A-Z0-9]{6,13}\b/g;

// Bank account: ABA routing number (exactly 9 digits with valid checksum)
// OR a longer digit run preceded by an account-signaling keyword. We err
// generously here because chat messages should never carry raw account
// numbers -- the cost of a false positive (masking a unit number) is far
// lower than the cost of leaking a DDA.
const BANK_KEYWORDS =
  '(?:account|acct|routing|aba|bank|checking|savings)(?:\\s+(?:number|#|no))?\\s*[:#]?';
// Keyword-gated bank-account candidate (8-17 digits, optionally
// hyphenated). Anchored to a bank-context keyword so generic 12-digit
// runs in lease numbers don't trip.
const BANK_KEYWORD_RE = new RegExp(
  `${BANK_KEYWORDS}\\s*([0-9][0-9\\s-]{6,18}[0-9])`,
  'gi',
);
// Standalone ABA routing: exactly 9 digits with valid mod-10 checksum.
// The checksum is rare enough by chance that an unguarded 9-digit run
// passing it is overwhelmingly likely to be a real ABA, NOT a coincidence.
const ABA_CANDIDATE_RE = /\b\d{9}\b/g;

/**
 * Luhn-mod-10 check for a digit-only string. Returns true iff the
 * number is a valid Luhn-checksum-passing sequence of 13-19 digits.
 *
 * Used to gate card-number scrubbing so we don't redact arbitrary 16-digit
 * order IDs or invoice numbers.
 */
function isLuhnValid(digits: string): boolean {
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48;
    if (n < 0 || n > 9) return false;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/**
 * ABA-mod-10 routing-number checksum. The ABA algorithm is a fixed-weight
 * sum where the weights are (3, 7, 1) repeating across the 9 digits. The
 * total must be divisible by 10. This is restrictive enough that random
 * 9-digit runs almost never pass.
 */
function isAbaValid(digits: string): boolean {
  if (digits.length !== 9) return false;
  const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const n = digits.charCodeAt(i) - 48;
    if (n < 0 || n > 9) return false;
    sum += n * weights[i]!;
  }
  return sum % 10 === 0;
}

function scrubPhones(input: string): string {
  return input
    .replace(E164_RE, '[FILTERED:phone]')
    .replace(US_PHONE_RE, '[FILTERED:phone]');
}

function scrubCardNumbers(input: string): string {
  return input.replace(CARD_CANDIDATE_RE, (match) => {
    const digits = match.replace(/[ -]/g, '');
    return isLuhnValid(digits) ? '[FILTERED:card]' : match;
  });
}

function scrubBankAccounts(input: string): string {
  // Keyword-anchored pass first so a "routing number" prefix is consumed
  // before the ABA-checksum pass walks the same 9-digit run.
  let out = input.replace(BANK_KEYWORD_RE, (match) => {
    return match.replace(/[0-9][0-9\s-]{6,18}[0-9]/, '[FILTERED:bank-account]');
  });
  out = out.replace(ABA_CANDIDATE_RE, (match) =>
    isAbaValid(match) ? '[FILTERED:bank-account]' : match,
  );
  return out;
}

function scrubDriversLicense(input: string): string {
  let out = input.replace(DL_KEYWORD_RE, (match, _candidate: string) => {
    // Replace only the candidate portion (group 1) with the sentinel,
    // preserving the keyword prefix so context like "DL: " survives.
    return match.replace(/[A-Z]{1,2}[0-9A-Z]{4,12}[0-9]/, '[FILTERED:dl]');
  });
  out = out.replace(DL_BARE_RE, '[FILTERED:dl]');
  return out;
}

function scrubDob(input: string): string {
  return input.replace(DOB_RE, '[FILTERED:dob]');
}

function scrubSecrets(input: string): string {
  let out = input;
  out = out.replace(STRIPE_SECRET_KEY_RE, '[FILTERED:secret]');
  out = out.replace(STRIPE_WEBHOOK_SECRET_RE, '[FILTERED:secret]');
  out = out.replace(BEARER_TOKEN_RE, 'Bearer [FILTERED:secret]');
  out = out.replace(SECRET_KEYWORD_RE, (match, candidate: string) =>
    match.replace(candidate, '[FILTERED:secret]'),
  );
  return out;
}

/** Steps 2-8 of the order above, over a span that holds no uuid token. */
function scrubPiiClasses(input: string): string {
  if (input.length === 0) return input;
  let out = input;
  out = scrubCardNumbers(out);
  out = out.replace(SSN_RE, '[FILTERED:ssn]');
  out = scrubDob(out);
  out = scrubDriversLicense(out);
  out = scrubBankAccounts(out);
  out = out.replace(EMAIL_RE, '[FILTERED:email]');
  out = scrubPhones(out);
  return out;
}

/**
 * Run all string-content PII scrubbers on a string. Returns the input
 * unchanged if no PII shape matched.
 *
 * Order: secrets -> card -> SSN -> DOB -> drivers-license -> bank-account
 * -> email -> phone.
 *
 * Standalone uuid tokens pass through the PII classes verbatim, so a
 * system id embedded in free text keeps its value (#10657). The secrets
 * pass still sees them. See the "Order in `scrubPiiInString` matters"
 * note above.
 *
 * Only call this on subtrees known to carry free-form text. Calling it on
 * structured fields like `path`, or on non-uuid ids with long digit runs,
 * may produce false positives.
 */
export function scrubPiiInString(input: string): string {
  if (typeof input !== 'string' || input.length === 0) return input;
  // ⛔ Secrets FIRST, over the WHOLE string -- before the uuid carve-out,
  // so `Bearer <uuid>` / `api_key=<uuid>` are still redacted.
  const afterSecrets = scrubSecrets(input);
  // ⛔ A uuid that sits inside an email address (`<uuid>@x.com`,
  // `a@mail.<uuid>.example.com`) is NOT carved out: splitting the address
  // around it would hide the rest of it from the email pass.
  const emailSpans: Array<[number, number]> = [];
  const emailRe = new RegExp(EMAIL_RE);
  let email: RegExpExecArray | null;
  while ((email = emailRe.exec(afterSecrets)) !== null) {
    emailSpans.push([email.index, email.index + email[0].length]);
  }
  const uuidRe = new RegExp(UUID_TOKEN_RE);
  let out = '';
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = uuidRe.exec(afterSecrets)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (emailSpans.some(([s, e]) => start < e && end > s)) continue;
    out += scrubPiiClasses(afterSecrets.slice(cursor, start)) + match[0];
    cursor = end;
  }
  return out + scrubPiiClasses(afterSecrets.slice(cursor));
}

/**
 * Convenience helper for callers (chat write path, audit-log redaction
 * pass) that want both layers applied to a single string: identical to
 * `scrubPiiInString`. Kept as a named export so call sites that
 * specifically want the "chat content scrub" semantic read naturally.
 */
export function redactChatMessage(content: string): string {
  return scrubPiiInString(content);
}
